import { useRef } from "react";

import { FormProvider } from "react-hook-form";

import { PRACTITIONER_PROFILE_FIELDS, PRACTITIONER_PROFILE_QUERY } from "@/graphql/accounts";
import useDjangoGraphqlForm from "@/hooks/form/useDjangoGraphqlForm";
import useScrollToError from "@/hooks/form/useScrollToError";
import { PanelBoxV2 } from "@/tpo/Boxes";
import EditFormButtons from "@/tpo/EditFormButtons";
import { gql } from "@apollo/client";

import ExpertPanel from "../sections/Expert";
import OtherDetails from "../sections/OtherDetails";
import YourDetailsSection from "../sections/YourDetails";

function getDefaultValues(practitionerProfile) {
  const yourDetailsFormFields = {
    yourDetailsFirstName: practitionerProfile.user.firstName || "",
    yourDetailsLastName: practitionerProfile.user.lastName || "",
    yourDetailsJobRole: practitionerProfile.jobRole || "",
    yourDetailsPhoneNumber: practitionerProfile.phoneNumber || ""
  };
  const expertFormFields = {
    expertPublic: practitionerProfile?.expert?.public || false,
    expertAvatar: {
      key: practitionerProfile?.expert?.avatar || "",
      fileUrl: practitionerProfile?.expert?.resizedAvatarUrl || ""
    },
    expertBio: practitionerProfile?.expert?.bio || "",
    expertLanguages: practitionerProfile?.expert?.languages?.map(l => l.id) || [],
    expertConsultationType: practitionerProfile?.expert?.consultationType || "",
    expertFeeLevel: practitionerProfile?.expert?.feeLevel || "",
    expertFreeConsultation: practitionerProfile?.expert?.freeConsultation || "",
    expertSupportedTests: practitionerProfile?.expert?.supportedTests?.map(st => st.id) || [],
    expertSpecialisms: practitionerProfile?.specialismsM2m?.map(s => s.id) || [],
    expertTestimonials: practitionerProfile?.expert?.testimonials
      ? [
          ...practitionerProfile?.expert.testimonials,
          ...[...Array(5 - practitionerProfile?.expert.testimonials.length).keys()].map(() => ({}))
        ].map(obj => ({
          id: obj.id ?? "",
          name: obj.name ?? "",
          detail: obj.detail ?? ""
        }))
      : [
          ...[...Array(5).keys()].map((_, idx) => ({
            id: "",
            name: "",
            detail: ""
          }))
        ]
  };
  const otherDetailsFormFields = {
    otherDetailsProfessionalBodies: practitionerProfile.professionalBodies || [],
    otherDetailsInstagram: practitionerProfile.instagram || "",
    otherDetailsLinkedin: practitionerProfile.linkedin || "",
    otherDetailsYoutube: practitionerProfile.youtube || "",
    otherDetailsWebsiteBlog: practitionerProfile.websiteBlog || "",
    otherDetailsBusinessLocationCity: practitionerProfile.businessLocationCity || ""
  };
  return {
    ...yourDetailsFormFields,
    ...expertFormFields,
    ...otherDetailsFormFields
  };
}

const SAVE_PROFILE_MUTATION = gql`
  mutation SaveProfileMutation($input: SaveProfileMutationInput!) {
    saveProfileMutation(input: $input) {
      practitionerProfile {
        ...PractitionerProfileFields
      }
      errors {
        field
        messages
      }
    }
  }
  ${PRACTITIONER_PROFILE_FIELDS}
`;

export default function PractitionerProfileForm({ practitionerProfile }) {
  const submitButtonRef = useRef();

  const api = useDjangoGraphqlForm({
    defaultValues: getDefaultValues(practitionerProfile),
    mutation: SAVE_PROFILE_MUTATION,
    mutationName: "saveProfileMutation",
    mutationOptions: {
      refetchQueries: [
        {
          query: PRACTITIONER_PROFILE_QUERY
        }
      ]
    },
    transformer: data => ({
      ...data,
      expertConsultationType: data.expertConsultationType
        ? data.expertConsultationType.join(",")
        : null,
      expertAvatar: data.expertAvatar?.key || ""
    }),
    handleSuccess: ({ data, api: { reset } }) => {
      reset(getDefaultValues(data.saveProfileMutation.practitionerProfile));
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(true);
        submitButtonRef.current.setPending(false);
      }
    },
    handleFailure: () => {
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(false);
        submitButtonRef.current.setPending(false);
      }
    }
  });

  useScrollToError(api);

  return (
    <>
      <FormProvider {...api.methods} submitButtonRef={submitButtonRef}>
        <form onSubmit={api.methods.handleSubmit(api.onSubmit)}>
          <YourDetailsSection
            fieldNames={{
              firstName: "yourDetailsFirstName",
              lastName: "yourDetailsLastName",
              jobRole: "yourDetailsJobRole",
              phoneNumber: "yourDetailsPhoneNumber"
            }}
          />
          <ExpertPanel practitionerProfile={practitionerProfile} />
          <OtherDetails practitionerProfile={practitionerProfile} />
          {api.methods.formState.isDirty && (
            <PanelBoxV2
              outer={{
                bg: "white",
                position: "sticky",
                bottom: 0,
                borderTopColor: "haze",
                borderTopStyle: "solid",
                borderTopWidth: 1,
                pt: [20, 20, 40],
                pb: [30, 30, 60],
                transition: "all 100ms ease-out",
                transform: "translateY(0)",
                opacity: 1,
                zIndex: 1
              }}
              gap={10}
              inner={{
                display: "flex",
                justifyContent: "center",
                gap: 20
              }}
            >
              <EditFormButtons
                cancelButtonProps={{
                  "data-component-name": "practitionerProfileCancelButton"
                }}
                saveButtonProps={{
                  "data-component-name": "practitionerProfileSaveButton"
                }}
              />
            </PanelBoxV2>
          )}
        </form>
      </FormProvider>
    </>
  );
}
